import { BaseComponent } from '../lib/BaseComponent.js';
import { api, Location, Extinguisher } from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Modal } from './shared/Modal.js';
import { Toast } from './shared/Toast.js';
import { buildExtinguisherForm, readExtinguisherForm } from '../lib/extinguisher-form.js';
import QRCode from 'qrcode';

/**
 * Location Detail Page Component
 * Shows location details with edit capabilities
 * Validates state (2-letter code) and zip (5 digits)
 */
export class LocationDetailPage extends BaseComponent {
  private isLoading = true;
  private location: Location | null = null;
  private extinguishers: Extinguisher[] = [];
  private errorMessage = '';
  private locationId: string = '';
  private isEditMode = false;
  private isNewLocation = false;

  // Form validation errors
  private validationErrors: { [key: string]: string } = {};

  // Modal instances
  private modal: Modal | null = null;
  private showAddExtinguisherModal = false;
  private showQRModal = false;
  private qrCodeDataUrl: string = '';

  connectedCallback(): void {
    // Get location ID from URL
    this.locationId = router.getParam('id') || '';
    this.isNewLocation = this.locationId === 'new';

    // Check for edit mode query param
    const params = new URLSearchParams(window.location.search);
    this.isEditMode = params.get('edit') === 'true' || this.isNewLocation;

    super.connectedCallback();
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'detail-container' });

    // Header with navigation
    const header = this.createElement('header', { className: 'detail-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const backBtn = this.createElement('button', { className: 'back-btn' }, '← Back to Locations');
    backBtn.addEventListener('click', () => router.navigate('/locations'));
    headerContent.appendChild(backBtn);

    const logo = this.createElement('h1', { className: 'logo' }, 'FireGuard');
    headerContent.appendChild(logo);

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/locations', () => this.handleLogout());
    headerContent.appendChild(nav);

    header.appendChild(headerContent);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'detail-main' });

    // Loading state
    if (this.isLoading && !this.isNewLocation) {
      const loading = this.createElement('div', { className: 'loading' }, 'Loading location details...');
      main.appendChild(loading);
      container.appendChild(main);
      this.shadow.appendChild(container);

      // Fetch data
      await this.loadLocationData();
      return;
    }

    // Error state
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      const retryBtn = this.createElement('button', { className: 'btn-primary' }, 'Back to List');
      retryBtn.addEventListener('click', () => router.navigate('/locations'));
      main.appendChild(error);
      main.appendChild(retryBtn);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Location details or form
    if (this.isEditMode) {
      const formSection = this.createFormSection();
      main.appendChild(formSection);
    } else {
      const detailsSection = this.createDetailsSection();
      main.appendChild(detailsSection);

      // Extinguishers at this location
      const extinguishersSection = this.createExtinguishersSection();
      main.appendChild(extinguishersSection);

      // Action buttons
      const actions = this.createActionsSection();
      main.appendChild(actions);
    }

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createDetailsSection(): HTMLElement {
    if (!this.location) return this.createElement('div', {});

    const section = this.createElement('section', { className: 'section details-section' });
    const sectionHeader = this.createElement('div', { className: 'section-header' });
    const title = this.createElement('h2', {}, this.location.name);
    sectionHeader.appendChild(title);
    section.appendChild(sectionHeader);

    // Details grid
    const grid = this.createElement('div', { className: 'details-grid' });

    const details = [
      { label: 'Location Name', value: this.location.name },
      { label: 'Address', value: this.location.address },
      { label: 'City', value: this.location.city },
      { label: 'State', value: this.location.state },
      { label: 'Zip Code', value: this.location.zip },
      {
        label: 'Date Added',
        value: new Date(this.location.created_at).toLocaleDateString()
      },
      {
        label: 'Extinguishers',
        value: `${this.extinguishers.length} extinguisher${this.extinguishers.length !== 1 ? 's' : ''}`
      },
    ];

    details.forEach(({ label, value }) => {
      const item = this.createElement('div', { className: 'detail-item' });
      const labelEl = this.createElement('div', { className: 'detail-label' }, label);
      const valueEl = this.createElement('div', { className: 'detail-value' }, value);
      item.appendChild(labelEl);
      item.appendChild(valueEl);
      grid.appendChild(item);
    });

    section.appendChild(grid);
    return section;
  }

  private createFormSection(): HTMLElement {
    const section = this.createElement('section', { className: 'section form-section' });
    const title = this.createElement('h2', {}, this.isNewLocation ? 'Add New Location' : 'Edit Location');
    section.appendChild(title);

    const form = this.createElement('form', { className: 'location-form' });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSave();
    });

    // Name field
    const nameGroup = this.createFormGroup(
      'name',
      'Location Name',
      'text',
      this.location?.name || '',
      'Enter location name',
      true
    );
    form.appendChild(nameGroup);

    // Address field
    const addressGroup = this.createFormGroup(
      'address',
      'Address',
      'text',
      this.location?.address || '',
      'Enter street address',
      true
    );
    form.appendChild(addressGroup);

    // City field
    const cityGroup = this.createFormGroup(
      'city',
      'City',
      'text',
      this.location?.city || '',
      'Enter city',
      true
    );
    form.appendChild(cityGroup);

    // State field
    const stateGroup = this.createFormGroup(
      'state',
      'State',
      'text',
      this.location?.state || '',
      'Two-letter state code (e.g., CA, NY)',
      true
    );
    form.appendChild(stateGroup);

    // Zip field
    const zipGroup = this.createFormGroup(
      'zip',
      'Zip Code',
      'text',
      this.location?.zip || '',
      '5-digit zip code',
      true
    );
    form.appendChild(zipGroup);

    // Form actions
    const actions = this.createElement('div', { className: 'form-actions' });
    const saveBtn = this.createElement('button', { type: 'submit', className: 'btn-primary btn-large' }, this.isNewLocation ? 'Create Location' : 'Save Changes');
    const cancelBtn = this.createElement('button', { type: 'button', className: 'btn-secondary btn-large' }, 'Cancel');
    cancelBtn.addEventListener('click', () => {
      if (this.isNewLocation) {
        router.navigate('/locations');
      } else {
        router.navigate(`/locations/${this.locationId}`);
      }
    });
    actions.appendChild(saveBtn);
    actions.appendChild(cancelBtn);
    form.appendChild(actions);

    section.appendChild(form);
    return section;
  }

  private createFormGroup(
    name: string,
    label: string,
    type: string,
    value: string,
    placeholder: string,
    required: boolean
  ): HTMLElement {
    const group = this.createElement('div', { className: 'form-group' });

    const labelEl = this.createElement('label', { className: 'form-label', htmlFor: name }, label);
    if (required) {
      const requiredMark = this.createElement('span', { className: 'required-mark' }, '*');
      labelEl.appendChild(requiredMark);
    }
    group.appendChild(labelEl);

    const input = this.createElement('input', {
      type,
      id: name,
      name,
      className: 'form-input',
      placeholder,
      required: required ? 'required' : undefined,
    }) as HTMLInputElement;
    input.value = value;

    // Add validation hints
    if (name === 'state') {
      input.maxLength = 2;
      input.addEventListener('input', (e) => {
        const val = (e.target as HTMLInputElement).value;
        (e.target as HTMLInputElement).value = val.toUpperCase();
      });
    } else if (name === 'zip') {
      input.maxLength = 5;
      input.addEventListener('input', (e) => {
        const val = (e.target as HTMLInputElement).value;
        (e.target as HTMLInputElement).value = val.replace(/[^0-9]/g, '');
      });
    }

    group.appendChild(input);

    // Show validation error if exists
    if (this.validationErrors[name]) {
      const errorEl = this.createElement('div', { className: 'field-error' }, this.validationErrors[name]);
      group.appendChild(errorEl);
    }

    return group;
  }

  private createExtinguishersSection(): HTMLElement {
    const section = this.createElement('section', { className: 'section extinguishers-section' });

    // Header with title and add button
    const header = this.createElement('div', { className: 'section-title-bar' });
    const title = this.createElement('h3', {}, `Fire Extinguishers (${this.extinguishers.length})`);
    const addBtn = this.createElement('button', { className: 'btn-primary' }, '+ Add Extinguisher');
    addBtn.addEventListener('click', () => this.openAddExtinguisherModal());
    header.appendChild(title);
    header.appendChild(addBtn);
    section.appendChild(header);

    if (this.extinguishers.length === 0) {
      const empty = this.createElement('div', { className: 'empty-message' }, 'No fire extinguishers at this location yet');
      section.appendChild(empty);
      return section;
    }

    // Extinguishers list
    const list = this.createElement('div', { className: 'extinguishers-list' });

    this.extinguishers.forEach(ext => {
      const card = this.createElement('div', { className: 'extinguisher-card' });

      const cardHeader = this.createElement('div', { className: 'card-header' });
      const qrCode = this.createElement('div', { className: 'card-qr' }, ext.qr_code);
      const badge = this.createElement('span', {
        className: `status-badge status-${ext.compliance_status}`,
      }, ext.compliance_status.toUpperCase());
      cardHeader.appendChild(qrCode);
      cardHeader.appendChild(badge);
      card.appendChild(cardHeader);

      const cardBody = this.createElement('div', { className: 'card-body' });
      const type = this.createElement('div', {}, `Type: ${ext.extinguisher_type}`);
      const size = this.createElement('div', {}, `Size: ${ext.size_lbs} lbs`);
      const lastInspection = this.createElement('div', {},
        `Last Inspection: ${ext.last_monthly_inspection
          ? formatDate(ext.last_monthly_inspection)
          : 'Never'
        }`
      );
      cardBody.appendChild(type);
      cardBody.appendChild(size);
      cardBody.appendChild(lastInspection);
      card.appendChild(cardBody);

      const cardFooter = this.createElement('div', { className: 'card-footer' });
      const viewBtn = this.createElement('button', { className: 'btn-link' }, 'View Details →');
      viewBtn.addEventListener('click', () => router.navigate(`/extinguishers/${ext.id}`));
      cardFooter.appendChild(viewBtn);
      card.appendChild(cardFooter);

      list.appendChild(card);
    });

    section.appendChild(list);
    return section;
  }

  private createActionsSection(): HTMLElement {
    const section = this.createElement('div', { className: 'actions-section' });

    const editBtn = this.createElement('button', { className: 'btn-primary btn-large' }, '✏️ Edit Location');
    editBtn.addEventListener('click', () => {
      router.navigate(`/locations/${this.locationId}?edit=true`);
    });

    const qrBtn = this.createElement('button', { className: 'btn-secondary btn-large' }, '📱 Generate QR Code');
    qrBtn.addEventListener('click', () => this.generateQRCode());

    const printBtn = this.createElement('button', { className: 'btn-secondary btn-large' }, '🖨️ Print QR labels');
    printBtn.addEventListener('click', () => this.printLabel());

    const deleteBtn = this.createElement('button', { className: 'btn-danger btn-large' }, '🗑️ Delete Location');
    deleteBtn.addEventListener('click', () => this.handleDelete());

    section.appendChild(editBtn);
    section.appendChild(qrBtn);
    section.appendChild(printBtn);
    section.appendChild(deleteBtn);

    return section;
  }

  private validateForm(formData: FormData): boolean {
    this.validationErrors = {};
    let isValid = true;

    // Name validation
    const name = formData.get('name') as string;
    if (!name || name.trim().length === 0) {
      this.validationErrors['name'] = 'Location name is required';
      isValid = false;
    }

    // Address validation
    const address = formData.get('address') as string;
    if (!address || address.trim().length === 0) {
      this.validationErrors['address'] = 'Address is required';
      isValid = false;
    }

    // City validation
    const city = formData.get('city') as string;
    if (!city || city.trim().length === 0) {
      this.validationErrors['city'] = 'City is required';
      isValid = false;
    }

    // State validation (2-letter code)
    const state = formData.get('state') as string;
    if (!state || state.trim().length === 0) {
      this.validationErrors['state'] = 'State is required';
      isValid = false;
    } else if (!/^[A-Z]{2}$/.test(state.trim())) {
      this.validationErrors['state'] = 'State must be a 2-letter code (e.g., CA, NY)';
      isValid = false;
    }

    // Zip validation (5 digits)
    const zip = formData.get('zip') as string;
    if (!zip || zip.trim().length === 0) {
      this.validationErrors['zip'] = 'Zip code is required';
      isValid = false;
    } else if (!/^\d{5}$/.test(zip.trim())) {
      this.validationErrors['zip'] = 'Zip code must be 5 digits';
      isValid = false;
    }

    return isValid;
  }

  private async handleSave(): Promise<void> {
    const form = this.shadow.querySelector('form');
    if (!form) return;

    const formData = new FormData(form);

    // Validate form
    if (!this.validateForm(formData)) {
      // Re-render to show validation errors
      this.shadow.innerHTML = '';
      await this.render();
      return;
    }

    try {
      const locationData = {
        name: (formData.get('name') as string).trim(),
        address: (formData.get('address') as string).trim(),
        city: (formData.get('city') as string).trim(),
        state: (formData.get('state') as string).trim().toUpperCase(),
        zip: (formData.get('zip') as string).trim(),
      };

      if (this.isNewLocation) {
        // Create new location
        const newLocation = await api.createLocation(locationData);
        router.navigate(`/locations/${newLocation.id}`);
      } else {
        // Update existing location
        await api.updateLocation(this.locationId, locationData);
        router.navigate(`/locations/${this.locationId}`);
      }
    } catch (error) {
      this.errorMessage = (error as Error).message || 'Failed to save location';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.location) return;

    const extCount = this.extinguishers.length;
    let confirmMessage = `Are you sure you want to delete "${this.location.name}"?`;

    if (extCount > 0) {
      confirmMessage += `\n\nWarning: This location has ${extCount} fire extinguisher${extCount > 1 ? 's' : ''}. Deleting this location may affect extinguisher records.`;
    }

    confirmMessage += '\n\nThis action cannot be undone.';

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await api.deleteLocation(this.locationId);
      router.navigate('/locations');
    } catch (error) {
      this.errorMessage = (error as Error).message || 'Failed to delete location';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  private async loadLocationData(): Promise<void> {
    try {
      // Load location details
      this.location = await api.getLocation(this.locationId);

      // Load extinguishers at this location
      const response = await api.getExtinguishers();
      this.extinguishers = response.extinguishers.filter(
        ext => ext.location_id === this.locationId
      );

      this.isLoading = false;
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Failed to load location';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  /**
   * Open modal to add new extinguisher
   */
  private openAddExtinguisherModal(): void {
    if (!this.modal) {
      this.modal = document.createElement('app-modal') as Modal;
      document.body.appendChild(this.modal);
    }

    const form = this.createAddExtinguisherForm();
    const footer = this.createAddExtinguisherFooter(form);

    this.modal.open({
      title: 'Add Fire Extinguisher',
      content: form,
      footer,
      size: 'medium',
      onClose: () => {
        this.validationErrors = {};
      }
    });
  }

  /**
   * Create form for adding extinguisher (shared builder, fixed to this location)
   */
  private createAddExtinguisherForm(): HTMLElement {
    return buildExtinguisherForm({ locationId: this.locationId });
  }

  /**
   * Create footer for add extinguisher modal
   */
  private createAddExtinguisherFooter(form: HTMLElement): HTMLElement {
    const footer = document.createElement('div');
    footer.style.display = 'flex';
    footer.style.gap = '12px';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn-secondary';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      this.modal?.close();
    });

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn-primary';
    saveBtn.textContent = 'Add Extinguisher';
    saveBtn.addEventListener('click', () => {
      const formElement = form as HTMLFormElement;
      if (formElement.checkValidity()) {
        this.handleAddExtinguisher(formElement);
      } else {
        formElement.reportValidity();
      }
    });

    footer.appendChild(cancelBtn);
    footer.appendChild(saveBtn);

    return footer;
  }

  /**
   * Handle adding new extinguisher
   */
  private async handleAddExtinguisher(form: HTMLFormElement): Promise<void> {
    try {
      const newExtinguisher = await api.createExtinguisher(readExtinguisherForm(form));
      Toast.success(`Added ${newExtinguisher.qr_code}`);
      this.modal?.close();

      // Navigate to the new extinguisher detail page
      router.navigate(`/extinguishers/${newExtinguisher.id}`);
    } catch (error) {
      const err = error as Error & { code?: string; upgradeUrl?: string };
      if (err.code === 'DUPLICATE_QR_CODE') {
        Toast.error('That QR code is already in use. Choose a different tag.');
        (form.querySelector('#fg-qr') as HTMLInputElement | null)?.focus();
        return;
      }
      Toast.error(err.message || 'Failed to add extinguisher');
      if (err.code === 'PLAN_LIMIT') {
        this.modal?.close();
        router.navigate(err.upgradeUrl || '/billing');
      }
    }
  }

  /**
   * Generate QR code for location
   */
  private async generateQRCode(): Promise<void> {
    if (!this.location) return;

    try {
      // Generate QR code containing location URL
      const url = `${window.location.origin}/locations/${this.locationId}`;
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      this.qrCodeDataUrl = qrDataUrl;

      // Create QR display content
      const content = document.createElement('div');
      content.style.textAlign = 'center';

      const img = document.createElement('img');
      img.src = qrDataUrl;
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      content.appendChild(img);

      const locationInfo = document.createElement('div');
      locationInfo.style.marginTop = '16px';
      locationInfo.style.fontSize = '14px';
      locationInfo.style.color = '#6b7280';
      locationInfo.innerHTML = `
        <p style="margin: 4px 0;"><strong>${this.location.name}</strong></p>
        <p style="margin: 4px 0;">${this.location.address}</p>
        <p style="margin: 4px 0;">${this.location.city}, ${this.location.state} ${this.location.zip}</p>
      `;
      content.appendChild(locationInfo);

      // Create footer with download button
      const footer = document.createElement('div');
      footer.style.display = 'flex';
      footer.style.gap = '12px';
      footer.style.justifyContent = 'flex-end';

      const downloadBtn = document.createElement('button');
      downloadBtn.className = 'btn-primary';
      downloadBtn.textContent = 'Download QR Code';
      downloadBtn.addEventListener('click', () => this.downloadQRCode());

      const closeBtn = document.createElement('button');
      closeBtn.className = 'btn-secondary';
      closeBtn.textContent = 'Close';
      closeBtn.addEventListener('click', () => this.modal?.close());

      footer.appendChild(downloadBtn);
      footer.appendChild(closeBtn);

      if (!this.modal) {
        this.modal = document.createElement('app-modal') as Modal;
        document.body.appendChild(this.modal);
      }

      this.modal.open({
        title: 'Location QR Code',
        content,
        footer,
        size: 'small'
      });

      Toast.success('QR Code generated!');
    } catch (error) {
      Toast.error('Failed to generate QR code');
      console.error(error);
    }
  }

  /**
   * Download QR code as PNG
   */
  private downloadQRCode(): void {
    if (!this.qrCodeDataUrl || !this.location) return;

    const link = document.createElement('a');
    link.href = this.qrCodeDataUrl;
    link.download = `location-${this.location.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-qr.png`;
    link.click();

    Toast.success('QR Code downloaded!');
  }

  /**
   * Print location label
   */
  /**
   * Print a QR label sheet for every extinguisher at this location. Each label
   * encodes https://<host>/scan?code=<qr_code> so any phone camera opens the
   * inspection flow; the code is printed in plain text for manual entry.
   */
  private async printLabel(): Promise<void> {
    if (!this.location) return;
    if (this.extinguishers.length === 0) {
      Toast.info('Add extinguishers to this location first, then print their labels.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Toast.error('Please allow popups to print labels');
      return;
    }
    try {
      const origin = window.location.origin;
      const labels = await Promise.all(
        this.extinguishers.map(async (ext) => {
          const dataUrl = await QRCode.toDataURL(`${origin}/scan?code=${encodeURIComponent(ext.qr_code)}`, { width: 260, margin: 1, errorCorrectionLevel: 'M' });
          return { ext, dataUrl };
        })
      );
      const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const cards = labels
        .map(
          ({ ext, dataUrl }) => `<div class="label">
  <div class="brand">FireGuard <span>Inspection Point</span></div>
  <img src="${dataUrl}" alt="QR code for ${esc(ext.qr_code)}">
  <div class="code">${esc(ext.qr_code)}</div>
  <div class="meta">${esc(ext.extinguisher_type)} &middot; ${esc(ext.size_lbs)} lb<br>${esc(this.location!.name)} &middot; ${esc(ext.room_area)}</div>
  <div class="hint">Scan with your phone camera to record the monthly check, or enter the code at ${esc(window.location.host)}/scan</div>
</div>`
        )
        .join('');
      const doc = printWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>QR labels - ${esc(this.location.name)}</title>
<style>
  @page { size: letter; margin: 0.4in; }
  body { font-family: -apple-system, Arial, sans-serif; margin: 0; color: #111; }
  .toolbar { padding: 12px 16px; background: #f3f4f6; display: flex; gap: 12px; align-items: center; font-size: 14px; }
  .toolbar button { padding: 8px 14px; border: 0; border-radius: 6px; background: #b91c1c; color: #fff; font-weight: 600; cursor: pointer; }
  .sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.2in; padding: 0.3in; }
  .label { border: 2px solid #111; border-radius: 8px; padding: 10px 8px; text-align: center; break-inside: avoid; page-break-inside: avoid; }
  .brand { font-weight: 800; font-size: 13px; letter-spacing: 0.02em; color: #b91c1c; }
  .brand span { color: #111; font-weight: 600; }
  .label img { width: 1.6in; height: 1.6in; display: block; margin: 6px auto; }
  .code { font-family: Menlo, monospace; font-size: 18px; font-weight: 700; letter-spacing: 0.06em; }
  .meta { font-size: 11px; color: #374151; margin-top: 4px; line-height: 1.3; }
  .hint { font-size: 8.5px; color: #6b7280; margin-top: 6px; line-height: 1.3; }
  @media print { .toolbar { display: none; } .sheet { padding: 0; } }
</style></head><body>
<div class="toolbar"><strong>${labels.length} label${labels.length === 1 ? '' : 's'}</strong> for ${esc(this.location.name)}. Print on letter paper or 2x2 in label stock. <button onclick="window.print()">Print</button></div>
<div class="sheet">${cards}</div>
</body></html>`);
      doc.close();
    } catch (error) {
      console.error('Label sheet error:', error);
      Toast.error('Could not generate labels');
      printWindow.close();
    }
  }

  private handleLogout(): void {
    api.logout();
    router.navigate('/login');
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .detail-container {
        min-height: 100vh;
        background: var(--gray-50);
      }

      .detail-header {
        background: white;
        border-bottom: 1px solid var(--gray-200);
        padding: var(--spacing-md) var(--spacing-lg);
      }

      .header-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .back-btn {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .back-btn:hover {
        background: var(--gray-50);
      }

      .logo {
        font-size: 24px;
        font-weight: 700;
        color: var(--danger);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: var(--spacing-md);
      }

      .btn-secondary {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }

      .detail-main {
        max-width: 1200px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--gray-500);
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .section {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        margin-bottom: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .section-header {
        margin-bottom: var(--spacing-lg);
        padding-bottom: var(--spacing-md);
        border-bottom: 2px solid var(--gray-200);
      }

      .section-header h2 {
        font-size: 32px;
        font-weight: 700;
        color: var(--gray-900);
        margin: 0;
      }

      .section-title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-lg);
      }

      .section-title-bar h3 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin: 0;
      }

      .section-title-bar .btn-primary {
        padding: 10px 20px;
        font-size: 14px;
      }

      .details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: var(--spacing-lg);
      }

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .detail-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--gray-500);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .detail-value {
        font-size: 16px;
        color: var(--gray-900);
        font-weight: 500;
      }

      /* Form Styles */
      .form-section h2 {
        font-size: 28px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-lg);
      }

      .location-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-lg);
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .form-label {
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
      }

      .required-mark {
        color: var(--danger);
        margin-left: 4px;
      }

      .form-input {
        padding: 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 16px;
        transition: border-color 0.2s;
      }

      .form-input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .field-error {
        color: var(--danger);
        font-size: 14px;
        font-weight: 500;
      }

      .form-actions {
        display: flex;
        gap: var(--spacing-md);
        margin-top: var(--spacing-md);
      }

      /* Extinguishers Section */
      .extinguishers-section h3 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-lg);
      }

      .empty-message {
        text-align: center;
        padding: var(--spacing-xl);
        color: var(--gray-500);
      }

      .extinguishers-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: var(--spacing-md);
      }

      .extinguisher-card {
        border: 1px solid var(--gray-200);
        border-radius: var(--border-radius);
        overflow: hidden;
        transition: box-shadow 0.2s;
      }

      .extinguisher-card:hover {
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-md);
        background: var(--gray-50);
        border-bottom: 1px solid var(--gray-200);
      }

      .card-qr {
        font-weight: 600;
        color: var(--gray-900);
      }

      .status-badge {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }

      .status-compliant {
        background: var(--success);
      }

      .status-warning {
        background: #f59e0b;
      }

      .status-overdue {
        background: var(--danger);
      }

      .card-body {
        padding: var(--spacing-md);
        font-size: 14px;
        color: var(--gray-700);
        line-height: 1.6;
      }

      .card-footer {
        padding: var(--spacing-md);
        border-top: 1px solid var(--gray-200);
        text-align: right;
      }

      .btn-link {
        background: transparent;
        border: none;
        color: var(--primary);
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        text-decoration: none;
      }

      .btn-link:hover {
        text-decoration: underline;
      }

      /* Action Buttons */
      .actions-section {
        display: flex;
        gap: var(--spacing-md);
        flex-wrap: wrap;
      }

      .btn-primary, .btn-danger {
        padding: 16px 32px;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-large {
        flex: 1;
        min-width: 200px;
      }

      .btn-primary {
        background: var(--primary);
        color: white;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-danger {
        background: var(--danger);
        color: white;
      }

      .btn-danger:hover {
        background: #dc2626;
      }

      @media (max-width: 768px) {
        .details-grid {
          grid-template-columns: 1fr;
        }

        .extinguishers-list {
          grid-template-columns: 1fr;
        }

        .actions-section,
        .form-actions {
          flex-direction: column;
        }

        .btn-large {
          width: 100%;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('location-detail-page', LocationDetailPage);
